import { buildLocationFilterOptions, getExhaustivePickupLocations } from "../utils/locations.js"

/** @typedef {{ display: string, value: string, disabled?: boolean }} FilterOption */

/** @typedef {{ label: string, displayLabel: string, chipType: "text" | "dropdown" }} OrderColumnFilterConfig */

export const DEFAULT_STATUS_OPTIONS = [
    { display: "Unconfirmed", value: "new" },
    { display: "Confirmed", value: "confirmed" },
    { display: "Transferring", value: "shipped" },
    { display: "Received", value: "received" },
    { display: "Weighed In", value: "weighed_in" },
    { display: "Partially Boxed", value: "partially_boxed_up" },
    { display: "All Boxed Up", value: "boxed_up" },
    { display: "Shipment Made", value: "shipment_made" },
    { display: "Invoiced", value: "invoiced" },
]

/** Statuses after "Received" — omitted from lodge order search filters. */
const LODGE_EXCLUDED_STATUS_VALUES = new Set([
    "weighed_in",
    "partially_boxed_up",
    "boxed_up",
    "shipment_made",
    "invoiced",
])

/** Lodge orders list: Unconfirmed through Received only. */
export const LODGE_STATUS_OPTIONS = DEFAULT_STATUS_OPTIONS.filter(
    (option) => !LODGE_EXCLUDED_STATUS_VALUES.has(option.value)
)

const DROPDOWN_LABELS = new Set(["status", "location_id", "ship_to"])

/** @type {Record<string, OrderColumnFilterConfig>} */
export const ORDER_COLUMN_FILTER_CONFIG = {
    location: { label: "location_id", displayLabel: "Location", chipType: "dropdown" },
    master: { label: "master", displayLabel: "Master #", chipType: "text" },
    status: { label: "status", displayLabel: "Status", chipType: "dropdown" },
    firstName: { label: "first_name", displayLabel: "First Name", chipType: "text" },
    lastName: { label: "last_name", displayLabel: "Last Name", chipType: "text" },
    fishTag: { label: "fish_tag", displayLabel: "Fish Tag", chipType: "text" },
    tripCode: { label: "trip_code", displayLabel: "Trip Code", chipType: "text" },
    sjNum: { label: "st_jeans_num", displayLabel: "SJ Num", chipType: "text" },
    shipTo: { label: "ship_to", displayLabel: "Ship To", chipType: "dropdown" },
}

/** @type {Record<string, string>} */
const ORDER_FILTER_API_LABEL_DISPLAY = Object.fromEntries(
    Object.values(ORDER_COLUMN_FILTER_CONFIG).map((config) => [config.label, config.displayLabel])
)

/**
 * Friendly UI label for a filter chip API property name.
 * @param {string} apiLabel
 */
export function getFilterChipDisplayLabel(apiLabel) {
    return ORDER_FILTER_API_LABEL_DISPLAY[apiLabel] ?? apiLabel
}

export function getOrderFilterLabelForColumn(columnKey) {
    return ORDER_COLUMN_FILTER_CONFIG[columnKey]?.label ?? null
}

export function isOrderColumnFilterable(columnKey) {
    return Boolean(ORDER_COLUMN_FILTER_CONFIG[columnKey])
}

/**
 * @param {string} columnKey
 * @param {{
 *   locations?: Array<{ id: number | string, name: string, is_lodge?: boolean, allow_pick_up?: boolean, pick_up_name?: string }>,
 *   statusOptions?: () => FilterOption[],
 * }} [context]
 */
export function buildOrderFilterChipForColumn(columnKey, context = {}) {
    const config = ORDER_COLUMN_FILTER_CONFIG[columnKey]
    if (!config) {
        return null
    }

    const baseChip = {
        label: config.label,
        displayLabel: config.displayLabel,
        command: config.chipType === "text" ? "contains" : "=",
        value: "",
        chipType: config.chipType,
    }

    return enrichOrderSearchChips([baseChip], context)[0] ?? null
}

/**
 * @param {import("./parseSearchString.js").SearchFilterChip[]} chips
 * @param {{
 *   locations?: Array<{ id: number | string, name: string, is_lodge?: boolean, allow_pick_up?: boolean, pick_up_name?: string }>,
 *   statusOptions?: () => FilterOption[],
 * }} [context]
 */
export function enrichOrderSearchChips(chips, context = {}) {
    const locations = context.locations ?? []
    const statusOptions = context.statusOptions ?? (() => DEFAULT_STATUS_OPTIONS)

    return chips.map((chip) => {
        const displayLabel = chip.displayLabel ?? getFilterChipDisplayLabel(chip.label)

        if (chip.label === "status") {
            return {
                ...chip,
                displayLabel,
                chipType: "dropdown",
                options: statusOptions(),
            }
        }
        if (chip.label === "location_id") {
            return {
                ...chip,
                displayLabel,
                chipType: "dropdown",
                options: buildLocationFilterOptions(locations),
            }
        }
        if (chip.label === "ship_to") {
            const pickupLocations = getExhaustivePickupLocations(locations)
            return {
                ...chip,
                displayLabel,
                chipType: "dropdown",
                options: [
                    { display: "Customer", value: "customer" },
                    ...pickupLocations.map((location) => ({
                        display: location.name,
                        value: String(location.id),
                    })),
                ],
            }
        }
        return {
            ...chip,
            displayLabel,
            chipType: DROPDOWN_LABELS.has(chip.label) ? "dropdown" : "text",
        }
    })
}

/**
 * @param {import("./parseSearchString.js").SearchFilterChip[]} chips
 * @returns {string[]}
 */
export function orderChipsToApiFilters(chips) {
    return chips.map((chip) => {
        const label = chip.label === "ship_to" ? "pick_up_location_id" : chip.label
        const value = chip.value === "customer" ? "" : chip.value
        return `{${label} ${chip.command} ${value}}`
    })
}
